/**
 * Confirmation gate for every destructive or visibility-changing action in the console
 * (docs/06 §1.4, QA iteration 2 UX-211).
 *
 * `window.confirm` blocks the renderer, cannot be styled, cannot say *what* is at stake and is
 * invisible to the accessibility audit — QA found "no `[role=alertdialog]` in the DOM". This is
 * the replacement: one `useConfirm()` that returns a promise, so a call site reads
 *
 *   if (!(await confirm({ title, body, confirmLabel, tone: 'danger' }))) return;
 *
 * The dialog is a real `role="alertdialog"`: labelled by its title, described by its body, modal,
 * Escape and the scrim cancel, Tab is trapped inside it and focus returns to the control that
 * opened it. A destructive dialog opens with **Cancel** focused; a neutral one (publish) opens on
 * its primary. `requireText` reproduces the "type the name" gate docs/06 §4 asks for on a delete.
 */
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createContext, useContext } from 'react';

import { useT } from '../../i18n';
import { Button } from './button';

export interface ConfirmRequest {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` paints the primary red and opens on Cancel. Defaults to `danger`. */
  tone?: 'danger' | 'primary';
  /** When set, the primary stays disabled until the operator types this string exactly. */
  requireText?: string;
  /** Hint shown above the `requireText` field. */
  requireTextHelp?: string;
}

type ConfirmFn = (request: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * `confirm(request)` — resolves `true` when the operator confirms, `false` on cancel, Escape or
 * the scrim. Outside a `ConfirmProvider` (a screen mounted on its own in a unit test) it resolves
 * `true`, so a missing provider can never silently swallow an action.
 */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  return fn ?? (() => Promise.resolve(true));
}

interface Pending {
  request: ConfirmRequest;
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (request) =>
      new Promise<boolean>((resolve) => {
        setPending({ request, resolve });
      }),
    [],
  );

  const settle = useCallback(
    (ok: boolean) => {
      setPending((current) => {
        current?.resolve(ok);
        return null;
      });
    },
    [setPending],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <ConfirmDialog key={pending.request.title} request={pending.request} onSettle={settle} />
      ) : null}
    </ConfirmContext.Provider>
  );
}

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  request,
  onSettle,
}: {
  request: ConfirmRequest;
  onSettle: (ok: boolean) => void;
}) {
  const { t } = useT();
  const titleId = useId();
  const bodyId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const [typed, setTyped] = useState('');

  const tone = request.tone ?? 'danger';
  const ready = request.requireText === undefined || typed === request.requireText;

  // Focus moves in, is trapped while the dialog is up, and returns to the opener on close.
  useEffect(() => {
    opener.current = document.activeElement;
    const target = panel.current?.querySelector<HTMLElement>(
      request.requireText !== undefined
        ? '[data-confirm-input]'
        : tone === 'danger'
          ? '[data-confirm-cancel]'
          : '[data-confirm-ok]',
    );
    target?.focus();
    return () => {
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) {
        back.focus();
        return;
      }
      // The opener went away with the action (a deleted row). Land inside whatever modal is
      // still open, or on the main region — never on `<body>` (UX-503).
      const fallback =
        document.querySelector<HTMLElement>('[role="dialog"] [data-drawer-title]') ??
        document.getElementById('main-content');
      fallback?.focus();
    };
  }, [request.requireText, tone]);

  /**
   * The dialog is the top layer, so it consumes Escape and Tab **in the capture phase** and
   * stops them there.
   *
   * QA 2026-09-15 **UX-503**: six of the gated actions live inside an editing drawer, and both
   * listeners used to sit on `document` in the bubble phase — the drawer's, registered first,
   * ran first. One Escape therefore closed the dialog *and* threw away the record being edited,
   * and because the drawer unmounted with it the `isConnected` guard below skipped the focus
   * restore and left `document.activeElement` on `<body>`. Capturing first and stopping the
   * event keeps Escape to exactly one meaning and keeps the drawer's own Tab trap out of the
   * way while the dialog is up.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onSettle(false);
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      event.stopPropagation();
      const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (stops.length === 0) return;
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onSettle]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-ink/40"
        data-testid="confirm-scrim"
        onClick={() => onSettle(false)}
      />
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={request.body ? bodyId : undefined}
        data-testid="confirm-dialog"
        className="relative w-full max-w-[440px] rounded-[12px] border border-line bg-panel p-5 shadow-xl"
      >
        <h2 id={titleId} className="text-[17px] font-bold text-ink">
          {request.title}
        </h2>
        {request.body ? (
          <div id={bodyId} className="mt-2 text-[13px] leading-5 text-muted">
            {request.body}
          </div>
        ) : null}

        {request.requireText !== undefined && (
          <label className="mt-4 block">
            <span className="block text-[12px] text-muted">
              {request.requireTextHelp ?? request.requireText}
            </span>
            <input
              data-confirm-input
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-3 text-[13px] text-ink outline-none focus-visible:border-pink"
            />
          </label>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button data-confirm-cancel variant="secondary" onClick={() => onSettle(false)}>
            {request.cancelLabel ?? t('common:actions.cancel')}
          </Button>
          <Button
            data-confirm-ok
            variant={tone === 'danger' ? 'danger' : 'primary'}
            disabled={!ready}
            onClick={() => onSettle(true)}
          >
            {request.confirmLabel ?? t('common:actions.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
